import React, { useState, useRef, useEffect } from 'react';

const AVATAR_COLORS = ['#2563eb','#7c3aed','#059669','#d97706','#dc2626','#0891b2','#be185d'];

const avatarColor = (str) => AVATAR_COLORS[(str || '?').charCodeAt(0) % AVATAR_COLORS.length];

const initials = (nombres, apellidos) => {
  const n = (nombres || '').trim()[0] || '';
  const a = (apellidos || '').trim()[0] || '';
  return (n + a).toUpperCase() || '?';
};

const tiempoRelativo = (fecha) => {
  if (!fecha) return '';
  const diff = (Date.now() - new Date(fecha)) / 1000;
  if (diff < 60) return 'ahora';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 172800) return 'ayer';
  return new Date(fecha).toLocaleDateString('es-EC', { day: '2-digit', month: '2-digit' });
};

const mensajePreview = (p) => {
  if (p.ultimo_mensaje) {
    const texto = p.ultimo_mensaje
      .replace(/^📱\s*(Primer contacto WA|Mensaje WA):\s*"?/, '')
      .replace(/^📤\s*Enviado:\s*"?/, '📤 ')
      .replace(/"$/, '');
    return texto.length > 50 ? texto.slice(0, 50) + '…' : texto;
  }
  return p.empresa || p.whatsapp_number || p.email || '';
};

const EtapaCombobox = ({ etapas, filtroEtapa, setFiltroEtapa }) => {
  const [abierto, setAbierto] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setAbierto(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const activa = etapas.find(e => e.nombre === filtroEtapa);
  const color = activa?.color || '#64748b';

  const opciones = [{ id: 'todas', nombre: 'Todas las etapas', color: '#64748b' }, ...etapas];

  return (
    <div ref={ref} style={{ position: 'relative', marginBottom: '0.6rem' }}>
      <button
        type="button"
        onClick={() => setAbierto(v => !v)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: '0.5rem',
          padding: '0.4rem 0.65rem', borderRadius: '10px',
          border: `1.5px solid ${filtroEtapa ? color : '#e2e8f0'}`,
          background: filtroEtapa ? `${color}12` : 'white',
          cursor: 'pointer', textAlign: 'left',
          boxShadow: abierto ? `0 0 0 3px ${color}22` : 'none',
          transition: 'all 0.15s',
        }}
      >
        <span style={{ width: '9px', height: '9px', borderRadius: '50%', background: color, flexShrink: 0, boxShadow: `0 0 0 3px ${color}22` }} />
        <span style={{ flex: 1, fontSize: '0.78rem', fontWeight: '700', color: filtroEtapa ? color : '#475569' }}>
          {activa?.nombre || 'Todas las etapas'}
        </span>
        <span style={{ fontSize: '0.7rem', color: '#94a3b8', transform: abierto ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>▾</span>
      </button>

      {abierto && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 200,
          background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb',
          boxShadow: '0 10px 28px rgba(15,23,42,0.14)', overflow: 'hidden', padding: '0.35rem',
        }}>
          {opciones.map(o => {
            const valor = o.id === 'todas' ? '' : o.nombre;
            const seleccionada = filtroEtapa === valor;
            return (
              <div
                key={o.id}
                onClick={() => { setFiltroEtapa(valor); setAbierto(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.55rem',
                  padding: '0.5rem 0.6rem', borderRadius: '8px', cursor: 'pointer',
                  background: seleccionada ? `${o.color}15` : 'transparent',
                }}
                onMouseEnter={e => { if (!seleccionada) e.currentTarget.style.background = '#f8fafc'; }}
                onMouseLeave={e => { if (!seleccionada) e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: o.color, flexShrink: 0 }} />
                <span style={{ flex: 1, fontSize: '0.8rem', fontWeight: seleccionada ? '700' : '500', color: seleccionada ? o.color : '#374151' }}>
                  {o.nombre}
                </span>
                {seleccionada && <span style={{ fontSize: '0.75rem', color: o.color, fontWeight: '700' }}>✓</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const CrmSidebar = ({
  prospectos, stats, selectedId, onSelect,
  buscar, setBuscar,
  filtroEtapa, setFiltroEtapa,
  filtroTipo, setFiltroTipo,
  onNuevo, loading, showDetail, isMobile, onToggleView,
  etapas = [],
}) => {
  const etapaInfo = (val) => {
    const found = etapas.find(e => e.nombre === val);
    return found ? { label: found.nombre, color: found.color } : { label: val || '?', color: '#6b7280' };
  };

  const tabs = [
    { value: '',          label: 'Todos',      count: stats.total || 0 },
    { value: 'prospecto', label: 'Prospectos', count: stats.prospectos || 0 },
    { value: 'cliente',   label: 'Clientes',   count: stats.clientes || 0 },
  ];

  return (
    <div style={{
      width: isMobile ? '100%' : '300px',
      flexShrink: 0,
      borderRight: '1px solid #e5e7eb',
      display: isMobile && showDetail ? 'none' : 'flex',
      flexDirection: 'column',
      background: 'white',
      height: '100%',
    }}>

      {/* ── Header ── */}
      <div style={{ padding: '0.875rem 1rem 0.75rem', borderBottom: '1px solid #f1f5f9' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.625rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: '#111827' }}>💬 Conversaciones</h2>
          </div>
          <div style={{ display: 'flex', gap: '0.35rem' }}>
            <button
              onClick={onToggleView}
              title="Vista pipeline"
              style={{
                background: '#f8fafc', color: '#374151',
                border: '1.5px solid #e2e8f0', borderRadius: '8px',
                padding: '0.35rem 0.6rem', cursor: 'pointer',
                fontSize: '0.78rem', fontWeight: '600',
                display: 'flex', alignItems: 'center', gap: '0.3rem',
              }}
            >📋 Etapas</button>
            <button
              className="btn btn-primary"
              onClick={onNuevo}
              style={{ fontSize: '0.78rem', padding: '0.35rem 0.75rem', borderRadius: '8px' }}
            >+ Nuevo</button>
          </div>
        </div>

        {/* Buscador */}
        <input
          type="text"
          placeholder="Buscar por nombre, WhatsApp..."
          value={buscar}
          onChange={e => setBuscar(e.target.value)}
          style={{
            width: '100%', padding: '0.5rem 0.75rem',
            border: '1.5px solid #e2e8f0', borderRadius: '8px',
            fontSize: '0.82rem', backgroundColor: '#f8fafc',
            boxSizing: 'border-box', outline: 'none', color: '#374151',
          }}
          onFocus={e => { e.target.style.borderColor = '#93c5fd'; e.target.style.backgroundColor = '#fff'; }}
          onBlur={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.backgroundColor = '#f8fafc'; }}
        />
      </div>

      {/* ── Tabs ── */}
      <div style={{ padding: '0.5rem 0.75rem 0', borderBottom: '1px solid #f1f5f9', background: '#fafafa' }}>
        <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '0.5rem' }}>
          {tabs.map(t => (
            <button
              key={t.value}
              onClick={() => setFiltroTipo(t.value)}
              style={{
                flex: 1, padding: '0.3rem 0.25rem', borderRadius: '6px',
                border: '1.5px solid',
                borderColor: filtroTipo === t.value ? '#2563eb' : '#e2e8f0',
                background: filtroTipo === t.value ? '#2563eb' : 'white',
                color: filtroTipo === t.value ? 'white' : '#64748b',
                fontSize: '0.72rem', fontWeight: '600', cursor: 'pointer',
              }}
            >
              {t.label}
              <span style={{ opacity: 0.75, marginLeft: '0.2rem' }}>({t.count})</span>
            </button>
          ))}
        </div>

        {/* Etapa combobox */}
        <EtapaCombobox etapas={etapas} filtroEtapa={filtroEtapa} setFiltroEtapa={setFiltroEtapa} />
      </div>

      {/* ── Lista de contactos ── */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div className="loading" />
          </div>
        ) : prospectos.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2.5rem 1rem', color: '#94a3b8' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>💬</div>
            <p style={{ fontSize: '0.85rem', margin: 0, fontWeight: '500' }}>
              {buscar || filtroEtapa || filtroTipo ? 'Sin resultados' : 'Sin contactos aún'}
            </p>
          </div>
        ) : (
          prospectos.map((p, idx) => {
            const etapa = etapaInfo(p.etapa);
            const ini   = initials(p.nombres, p.apellidos);
            const color = avatarColor(p.nombres);
            const tiempo  = tiempoRelativo(p.ultimo_mensaje_fecha || p.updated_at);
            const preview = mensajePreview(p);
            const nombre  = `${p.nombres}${p.apellidos ? ' ' + p.apellidos : ''}`;
            const isSelected = p.id === selectedId;

            return (
              <div
                key={p.id}
                onClick={() => onSelect(p.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.75rem',
                  padding: '0.75rem 1rem',
                  borderBottom: idx < prospectos.length - 1 ? '1px solid #f1f5f9' : 'none',
                  cursor: 'pointer',
                  background: isSelected ? '#eff6ff' : 'white',
                  borderLeft: `3px solid ${isSelected ? '#2563eb' : 'transparent'}`,
                  transition: 'background 0.1s',
                }}
                onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#f8fafc'; }}
                onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'white'; }}
              >
                {/* Avatar */}
                <div style={{
                  width: '40px', height: '40px', flexShrink: 0,
                  borderRadius: '50%', background: color, color: 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: '700', fontSize: '0.82rem', position: 'relative',
                }}>
                  {ini}
                  <span style={{
                    position: 'absolute', bottom: '1px', right: '1px',
                    width: '9px', height: '9px', borderRadius: '50%',
                    background: etapa.color, border: '2px solid white',
                  }} title={etapa.label} />
                </div>

                {/* Contenido */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.2rem' }}>
                    <span style={{
                      fontWeight: '600', fontSize: '0.85rem', color: '#111827',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {nombre}
                    </span>
                    <span style={{ fontSize: '0.68rem', color: '#94a3b8', flexShrink: 0, marginLeft: '0.4rem' }}>
                      {tiempo}
                    </span>
                  </div>
                  <span style={{
                    fontSize: '0.77rem', color: preview ? '#64748b' : '#cbd5e1',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    display: 'block',
                  }}>
                    {preview || 'Sin mensajes aún'}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default CrmSidebar;
